import React from "react";
import LoadingOverlay from "react-loading-overlay";
import { AgGridReact } from "ag-grid-react";
import { Row, Col } from "react-bootstrap";

import RetryRenderer from "../Renderers/RetryRenderer";
import "./RetryFailure.css";

const RetryFailure = ({ 
  responseData, 
  isLoading, 
  retryRequestHandler, 
  retryRequestError, 
  retryRequestSuccess 
}) => {

  const columnDefs = [
    {
      headerName: "External Vendor Id",
      field: "externalVendorId",
      width: 180,

      comparator: stringDataComparator,
    },
    {
      headerName: "Vendor Profile Id",
      field: "vendorProfileId",
      width: 180,
      comparator: numericDataComparator,
    },
    {
      headerName: "Vendor Corp Name",
      field: "vendorCorpName",
      width: 180,
      comparator: stringDataComparator,
    },
    {
      headerName: "Duns",
      field: "duns",
      width: 150,
      comparator: stringNumericDataComparator,
    },
    {
      headerName: "Selling Channel",
      field: "sellingChannel",
      width: 180,
      comparator: stringDataComparator,
    },
    {
      headerName: "Last Updated",
      field: "updatedDate",
      width: 150,
      sortingOrder: ["asc", "desc", null],
      comparator: dateComparator,
    },
    {
      headerName: "Payload",
      field: "payload",
      autoHeight: true,
      sortable: false,
      width: 350,
      cellClass: "cell-wrap-text",
    },
    {
      headerName: "",
      field: "retry",
      width: 130,
      resizable: false,
      sortable: false,
      filter: false,
      cellRendererFramework: RetryRenderer,
      cellRendererParams: {
        retryRequestHandler: retryRequestHandler,
      },
    },
  ];

  const defaultColDef = {
    lockVisible: true,
    suppressMovable: true,
    filter: true,
    sortable: true,
    resizable: true,
  };

  function stringDataComparator(valueA, valueB) {
    const valueALower =
      valueA && valueA !== "" ? valueA.toLowerCase().trim() : valueA;
    const valueBLower =
      valueB && valueB !== "" ? valueB.toLowerCase().trim() : valueB;
    if (valueALower && valueBLower) {
      return valueALower === valueBLower
        ? 0
        : valueALower.localeCompare(valueBLower, "en", { numeric: true });
    } else if (valueALower && (valueBLower === null || valueBLower === "")) {
      return -1;
    } else if ((valueALower === null || valueALower === "") && valueBLower) {
      return 1;
    } else {
      return -1;
    }
  }

  function stringNumericDataComparator(valueA, valueB) {
    const valueALower =
      valueA && valueA !== "" ? valueA.toLowerCase().trim() : valueA;
    const valueBLower =
      valueB && valueB !== "" ? valueB.toLowerCase().trim() : valueB;
    if (valueALower && valueBLower) {
      if (!isNaN(valueALower) && !isNaN(valueBLower)) {
        return parseInt(valueALower, 10) === parseInt(valueBLower, 10)
          ? 0
          : parseInt(valueALower, 10) > parseInt(valueBLower, 10)
          ? 1
          : -1;
      } else if (isNaN(valueALower) && !isNaN(valueBLower)) {
        return 1;
      } else if (!isNaN(valueALower) && isNaN(valueBLower)) {
        return -1;
      } else {
        return valueALower === valueBLower
          ? 0
          : valueALower.localeCompare(valueBLower, "en", { numeric: true });
      }
    } else if (valueALower && (valueBLower === null || valueBLower === "")) {
      return -1;
    } else if ((valueALower === null || valueALower === "") && valueBLower) {
      return 1;
    } else {
      return -1;
    }
  }

  function numericDataComparator(valueA, valueB) {
    valueA = valueA && valueA.toString();
    valueB = valueB && valueB.toString();
    //Split the values by separator " - " & " , "

    //Take the left side value
    const valueA0 =
      valueA && valueA.indexOf("-") > -1
        ? valueA.split("-")[0]
        : valueA && valueA.indexOf(",") > -1
        ? valueA.split(",")[0]
        : valueA;
    const valueB0 =
      valueB && valueB.indexOf("-") > -1
        ? valueB.split("-")[0]
        : valueB && valueB.indexOf(",") > -1
        ? valueB.split(",")[0]
        : valueB;

    //Take the right side value
    const valueA1 =
      valueA && valueA.indexOf("-") > -1
        ? valueA.split("-")[1]
        : valueA && valueA.indexOf(",") > -1
        ? valueA.split(",")[1]
        : null;
    const valueB1 =
      valueB && valueB.indexOf("-") > -1
        ? valueB.split("-")[1]
        : valueB && valueB.indexOf(",") > -1
        ? valueB.split(",")[1]
        : null;

    if (valueA0 && valueB0) {
      if (parseInt(valueA0.trim(), 10) === parseInt(valueB0.trim(), 10)) {
        if (valueA1 && valueB1 && !isNaN(valueA1) && !isNaN(valueB1)) {
          return parseInt(valueA1.trim(), 10) === parseInt(valueB1.trim(), 10)
            ? 0
            : parseInt(valueA1.trim(), 10) > parseInt(valueB1.trim(), 10)
            ? 1
            : -1;
        } else if (valueA1 && valueB1 && isNaN(valueA1) && isNaN(valueB1)) {
          return valueA1.trim().toLowerCase() === valueB1.trim().toLowerCase()
            ? 0
            : valueA1.trim().toLowerCase() > valueB1.trim().toLowerCase()
            ? 1
            : -1;
        } else {
          return 0;
        }
      } else {
        return parseInt(valueA0.trim(), 10) > parseInt(valueB0.trim(), 10)
          ? 1
          : -1;
      }
    } else if (valueA0 && valueB0 === null) {
      return -1;
    } else if (valueA0 === null && valueB0) {
      return 1;
    } else {
      return -1;
    }
  }
  
  function dateComparator(date1, date2) {
    let date1Number = Date.parse(date1);
    let date2Number = Date.parse(date2);
    if (
      (isNaN(date1Number) || date1Number === null) &&
      (isNaN(date2Number) || date2Number === null)
    ) {
      return 0;
    }
    if (isNaN(date1Number) || date1Number === null) {
      return -1;
    }
    if (isNaN(date2Number) || date2Number === null) {
      return 1;
    }
    return date1Number - date2Number;
  }

  return (
    <div className="retryFailure">
      {retryRequestError && (
        <Row className="py-0">
          <Col className="px-1">
            <p className="errorMessage w-100 mb-3">
              {retryRequestError}
            </p>
          </Col>
        </Row>
      )}
      {retryRequestSuccess && (
        <Row className="py-0">
          <Col className="px-1">
            <p className="successMessage w-100 mb-3">
              {retryRequestSuccess}
            </p>
          </Col>
        </Row>
      )}
      <LoadingOverlay active={isLoading} spinner>
        <div className="ag-theme-balham retryFailureTable">
          <AgGridReact
            columnDefs={columnDefs}
            rowData={responseData}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            paginationPageSize="20"
            pagination={true}
            overlayNoRowsTemplate={
              responseData.length > 0
                ? ""
                : "No Failure Status detail available"
            }
          />
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default RetryFailure;
